// Shared constants and helpers for the Sync Log page.
//
// Keep this file free of React / MUI imports so it can be reused from any
// component, drawer, slice, or test in the Sync Log module.
import { CalendarDate } from "@internationalized/date";
import { NETWORK_LABEL_MAP } from "../../../utils/networkConstants";
// Fallback text used whenever a field has no data.
// Pair with `naInputClass` (for FormInputs) or `NA_TEXT_CLASS` (for Typography) to render it italic.
export const NA_TEXT = "N/A";
export const NA_TEXT_CLASS = "process-log-na-text";
export const isEmpty = (value) => value === null || value === undefined || String(value).trim().length === 0;
export const withNA = (value) => {
    if (isEmpty(value))
        return NA_TEXT;
    return String(value).trim();
};
export const naInputClass = (value) => isEmpty(value) ? "process-log-na-italic" : "";
export const naSx = (value) => isEmpty(value) ? { fontStyle: "italic", color: "#9CA3AF" } : {};
// Display-only label overrides; the raw value is unchanged for filtering/backend.
export const TYPE_LABEL_MAP = {
    dailySync: "Daily Sync",
    historicalDataSchedular: "Backfill Sync",
    module_refresh: "Module Refresh",
    manual_refresh: "Module Refresh", // legacy value — keep so older rows still label correctly
    ltv_report: "LTV Sync",
    affiliateProjectionSchedular: "Affiliate Performance Scheduler"
};
export const MODULE_REFRESH_TYPE = "module_refresh";
// Fixed Sync Type choices for the create drawer dropdown.
// value = backend key (sent on create), label = display text.
export const SYNC_LOG_TYPE_OPTIONS = [
    { value: "historicalDataSchedular", label: "Backfill Sync" },
    { value: "cron", label: "Cron" },
    { value: "dailySync", label: "Daily Sync" },
    { value: MODULE_REFRESH_TYPE, label: "Module Refresh" },
];
// Convert camelCase / PascalCase backend values into space-separated, capitalized labels.
// Examples: "historicalDataSchedular" -> "Historical Data Schedular", "APIData" -> "API Data".
// Falls back to TYPE_LABEL_MAP overrides when present.
export const formatSyncType = (type) => {
    if (!type)
        return NA_TEXT;
    if (TYPE_LABEL_MAP[type])
        return TYPE_LABEL_MAP[type];
    const spaced = type
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
        .trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};
export const formatNetwork = (network) => {
    if (isEmpty(network))
        return NA_TEXT;
    return NETWORK_LABEL_MAP[network] || network;
};
// Formats an ISO date string as MM/DD/YYYY.
export const formatDate = (dateStr) => {
    if (!dateStr)
        return NA_TEXT;
    const parts = dateStr.split("T")[0].split("-");
    if (parts.length === 3)
        return `${parts[1]}/${parts[2]}/${parts[0]}`;
    return dateStr;
};
export const toCalendarDate = (dateStr) => {
    if (!dateStr)
        return null;
    const parts = dateStr.split("T")[0].split("-").map(Number);
    if (parts.length === 3 && parts[0] && parts[1] && parts[2])
        return new CalendarDate(parts[0], parts[1], parts[2]);
    return null;
};
export const fromCalendarDate = (date) => {
    if (!date)
        return "";
    return `${date.year}-${String(date.month).padStart(2, "0")}-${String(date.day).padStart(2, "0")}`;
};
// Pretty-prints an error detail string for the view drawers.
// 1) Tries strict JSON.parse -> re-stringify with 2-space indentation.
// 2) Falls back to unescaping common sequences (\n, \r\n, \t, \") for readability.
// Returns null when the value is empty or nothing could be improved, so the
// caller can surface a "nothing to format" message instead of a no-op.
export const formatErrorDetail = (text) => {
    if (!text || !text.trim())
        return null;
    const raw = text.trim();
    try {
        return JSON.stringify(JSON.parse(raw), null, 2);
    }
    catch {
        // not valid JSON — fall through to best-effort unescaping
    }
    const unescaped = raw
        .replace(/\\r\\n/g, "\n")
        .replace(/\\n/g, "\n")
        .replace(/\\t/g, "\t")
        .replace(/\\"/g, '"');
    return unescaped !== raw ? unescaped : null;
};
export const SYNC_LOG_STATUS_OPTIONS = [
    { value: "pending", label: "Pending" },
    { value: "processing", label: "Processing" },
    { value: "completed", label: "Completed" },
    { value: "error", label: "Error" },
];
// Status -> CommonChip palette index + label, per log type.
export const SYNC_LOG_STATUS_CHIP = {
    pending: { label: "Pending", index: 5 }, // blue-light
    processing: { label: "Processing", index: 1 }, // amber
    completed: { label: "Completed", index: 2 }, // green
    error: { label: "Error", index: 0 }, // red
};
export const ERROR_LOG_STATUS_CHIP = {
    error: { label: "Error", index: 0 }, // red
    working: { label: "Working", index: 1 }, // amber
    completed: { label: "Completed", index: 2 }, // green
};
